import { ATTRIBUTE_KINDS, ATTRIBUTE_TYPES, getAttributeTimeFormat, ROOT_STRUCT_CODE } from '@/entities/attributes';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type { ConversationAttrValueInput } from '@/entities/conversations';
import { parseTimeToSeconds, type TimeIntervalFormat } from '@/shared/lib';

/**
 * Вид поля, которым вводится одно значение атрибута. Выбирается по типу данных атрибута, а у
 * интервала времени — по действующему формату его показа: длительностью или числом секунд.
 */
export type AttributeValueKind =
  | 'text'
  | 'integer'
  | 'decimal'
  | 'duration'
  | 'boolean'
  | 'date'
  | 'timestamp'
  | 'select'
  | 'multiselect';

/** Значение в поле ввода — в том виде, в каком его держит поле, до приведения к формату сервера */
export type AttributeValueDraft = string | number | boolean | Array<string | number>;

/** Запись длительности в поле ввода: часы, минуты и секунды по две цифры */
const DURATION_PATTERN = /^\d{2}:[0-5]\d:[0-5]\d$/;

/** Атрибут, которому значение задаётся вручную: аналитический, корневой, не список и не удалён */
export const isManualValueAttribute = (attribute: ConversationAttributeItem): boolean =>
  attribute.type === ATTRIBUTE_KINDS.ANALYTICS &&
  (attribute.parentStructCode || ROOT_STRUCT_CODE) === ROOT_STRUCT_CODE &&
  !attribute.isList &&
  !attribute.isDeleted;

/** Вид поля значения атрибута; `null` — значение атрибута одним полем не вводится (структура) */
export const getAttributeValueKind = (
  attribute: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeValueKind | null => {
  if (getAttributeTimeFormat(attribute, timeDisplayFormat) === 'TIME') return 'duration';

  switch (attribute.dataType) {
    case ATTRIBUTE_TYPES.TEXT:
      return 'text';
    // Интервал времени, показываемый числом секунд, и вводится числом секунд
    case ATTRIBUTE_TYPES.TIME:
    case ATTRIBUTE_TYPES.INTEGER:
      return 'integer';
    case ATTRIBUTE_TYPES.DECIMAL:
      return 'decimal';
    case ATTRIBUTE_TYPES.CHECKBOX:
      return 'boolean';
    case ATTRIBUTE_TYPES.DATE:
      return 'date';
    case ATTRIBUTE_TYPES.TIMESTAMP:
      return 'timestamp';
    case ATTRIBUTE_TYPES.SELECT:
      return 'select';
    case ATTRIBUTE_TYPES.MULTISELECT:
      return 'multiselect';
    default:
      return null;
  }
};

/** Пустое значение поля: признак «да/нет» задан сразу, набор значений справочника пуст, прочее ждёт ввода */
export const createEmptyAttributeValue = (kind: AttributeValueKind): AttributeValueDraft => {
  if (kind === 'boolean') return false;
  if (kind === 'multiselect') return [];
  return '';
};

/** Число из поля ввода; `undefined` — поле пусто или число не читается */
const readNumber = (draft: AttributeValueDraft): number | undefined => {
  if (typeof draft === 'number') return Number.isFinite(draft) ? draft : undefined;
  if (typeof draft !== 'string' || draft.trim() === '') return undefined;
  const value = Number(draft);
  return Number.isFinite(value) ? value : undefined;
};

/** Строка из поля ввода; `undefined` — поле пусто */
const readString = (draft: AttributeValueDraft): string | undefined => {
  if (typeof draft === 'number') return String(draft);
  return typeof draft === 'string' && draft.trim() !== '' ? draft : undefined;
};

/**
 * Значение атрибута в формате записи на сервер ({@link ConversationAttrValueInput});
 * `undefined` — значение не задано или не читается по виду поля.
 */
export const toAttributeInputValue = (
  kind: AttributeValueKind,
  draft: AttributeValueDraft,
): ConversationAttrValueInput['value'] | undefined => {
  switch (kind) {
    case 'boolean':
      return typeof draft === 'boolean' ? draft : undefined;
    case 'multiselect':
      return Array.isArray(draft) && draft.length > 0 ? draft.map(String) : undefined;
    case 'integer': {
      const value = readNumber(draft);
      return value !== undefined && Number.isInteger(value) ? value : undefined;
    }
    case 'decimal':
      return readNumber(draft);
    case 'duration':
      return typeof draft === 'string' && DURATION_PATTERN.test(draft) ? parseTimeToSeconds(draft) : undefined;
    case 'text':
    case 'date':
    case 'timestamp':
    case 'select':
      return readString(draft);
  }
};
